import { lineasContexto } from "../../lib/document/contextoInclusion";
import { mesAnio } from "../../lib/document/fechas";
import type { CVDocument, Experiencia, Estudio } from "../../lib/document/model";
import { Bloque, TituloSeccion, type PropsSeleccion } from "./Bloque";

import "./documento.css";

/**
 * La hoja de vida completa, en pantalla y de una vez.
 *
 * Sustituye a los siete pasos: aqui esta todo el documento, y cada region es
 * pulsable para editarla en el sitio. Es tambien la vista previa, asi que sigue
 * al PDF que genera docs-microservices -- si las dos no coinciden, previsualizar
 * no sirve de nada.
 */

/** Etiqueta legible de un enlace, por dominio. Port de `getUrlLabel`. */
const etiquetaEnlace = (url: string): string => {
  const u = url.toLowerCase();
  const conocidos: [string, string][] = [
    ["linkedin.com", "LinkedIn"],
    ["github.com", "GitHub"],
    ["gitlab.com", "GitLab"],
    ["behance.net", "Behance"],
    ["dribbble.com", "Dribbble"],
    ["medium.com", "Medium"],
    ["stackoverflow.com", "Stack Overflow"],
    ["figma.com", "Figma"],
    ["kaggle.com", "Kaggle"],
    ["dev.to", "Dev.to"],
  ];
  return conocidos.find(([dominio]) => u.includes(dominio))?.[1] ?? "Enlace";
};

const hayTexto = (v?: string | null): boolean => Boolean(v && v.trim());

// --- encabezado --------------------------------------------------------------

const Encabezado = ({ doc, sel }: { doc: CVDocument; sel: PropsSeleccion }) => {
  const nombre = [doc.name, doc.last_names].filter(hayTexto).join(" ").trim();
  const ubicacion = [doc.city, doc.open_to_remote ? "Abierto a trabajo remoto" : ""]
    .filter(hayTexto)
    .join(" | ");

  return (
    <header className="doc-encabezado">
      {doc.is_inclusive && (
        <img
          className="doc-logo-inclusion"
          src="/logo-inclusion.png"
          alt="Símbolo de inclusión de personas con discapacidad"
        />
      )}

      <Bloque ruta="name" {...sel} bloque>
        <h1 className="doc-nombre">
          {nombre || <span className="doc-vacio">Tu nombre</span>}
        </h1>

        {hayTexto(doc.email) && (
          <p className="doc-contacto">
            <b>Email</b>: {doc.email}
          </p>
        )}
        {doc.phone?.value && (
          <p className="doc-contacto">
            <b>Teléfono</b>: {doc.phone.value}
          </p>
        )}
        {doc.links.map((enlace, i) => (
          <p className="doc-contacto" key={`${enlace}-${i}`}>
            <b>{etiquetaEnlace(enlace)}</b>: {enlace}
          </p>
        ))}
        {hayTexto(ubicacion) && (
          <p className="doc-contacto">
            <b>Ubicación</b>: {ubicacion}
          </p>
        )}
        {doc.is_inclusive && hayTexto(doc.disability_type) && (
          <p className="doc-contacto">
            <b>Tipo de discapacidad</b>: {doc.disability_type}
            {doc.disability_certificate ? " - Certificado vigente" : ""}
          </p>
        )}
      </Bloque>
    </header>
  );
};

// --- experiencia -------------------------------------------------------------

const ItemExperiencia = ({
  exp,
  indice,
  sel,
}: {
  exp: Experiencia;
  indice: number;
  sel: PropsSeleccion;
}) => {
  const cabecera = hayTexto(exp.position) || hayTexto(exp.company_name);
  const fechas = hayTexto(exp.start_date) || exp.current_job || hayTexto(exp.end_date);

  if (!cabecera && !fechas && !hayTexto(exp.details) && !hayTexto(exp.achievements)) {
    return null;
  }

  return (
    <div className="doc-item">
      <Bloque ruta={`experience[${indice}]`} {...sel} bloque>
        {cabecera && (
          <p>
            <span className="doc-item-cargo">{exp.position}</span>
            {exp.position && exp.company_name ? " · " : ""}
            {exp.company_name}
            {hayTexto(exp.sector) ? ` – Sector: ${exp.sector}` : ""}
          </p>
        )}

        {fechas && (
          <p className="doc-fechas">
            {mesAnio(exp.start_date)}
            {exp.current_job
              ? " - Actual"
              : hayTexto(exp.end_date)
                ? ` - ${mesAnio(exp.end_date)}`
                : ""}
          </p>
        )}
      </Bloque>

      {hayTexto(exp.details) && (
        <Bloque ruta={`experience[${indice}].details`} {...sel}>
          <p className="doc-texto">{exp.details}</p>
        </Bloque>
      )}

      {hayTexto(exp.achievements) && (
        <Bloque ruta={`experience[${indice}].achievements`} {...sel}>
          <p className="doc-etiqueta-logros">Logros</p>
          <p className="doc-texto">{exp.achievements}</p>
        </Bloque>
      )}
    </div>
  );
};

// --- educacion ---------------------------------------------------------------

const ItemEstudio = ({
  est,
  indice,
  sel,
}: {
  est: Estudio;
  indice: number;
  sel: PropsSeleccion;
}) => {
  const fin = est.is_current ? "Actual" : est.year;
  // Empieza y termina el mismo año: "2019", no "2019 - 2019". Igual en el PDF.
  const periodo =
    hayTexto(est.start_year) && hayTexto(fin) && est.start_year.trim() === fin.trim()
      ? est.start_year.trim()
      : [est.start_year, fin].filter(hayTexto).join(" - ");
  const detalle = [est.formation_level, est.format, est.country_name]
    .filter(hayTexto)
    .join(" · ");

  return (
    <div className="doc-item">
      <Bloque ruta={`education[${indice}]`} {...sel} bloque>
        {hayTexto(est.title_name) && (
          <p>
            <span className="doc-item-cargo">{est.title_name}</span>
            {est.institution_name ? ` · ${est.institution_name}` : ""}
          </p>
        )}
        {hayTexto(periodo) && <p className="doc-fechas">{periodo}</p>}
        {hayTexto(detalle) && <p className="doc-fechas">{detalle}</p>}
      </Bloque>
    </div>
  );
};

// --- listas ------------------------------------------------------------------

const ListaEnLinea = ({
  ruta,
  titulo,
  valores,
  sel,
}: {
  ruta: string;
  titulo: string;
  valores: string[];
  sel: PropsSeleccion;
}) => {
  // El documento se pinta con lo que haya, no con lo que deberia haber. Un
  // valor de forma inesperada aqui tumbaba React entero y dejaba la pantalla en
  // blanco: la hoja de vida de alguien no puede desaparecer porque un campo
  // llegue como texto en vez de lista.
  const lista = Array.isArray(valores)
    ? valores
    : typeof valores === "string"
      ? String(valores).split(/[,·]/).map((x) => x.trim()).filter(Boolean)
      : [];

  if (lista.length === 0) return null;
  return (
    <>
      <TituloSeccion>{titulo}</TituloSeccion>
      <Bloque ruta={ruta} {...sel}>
        <p className="doc-texto">{lista.join(" · ")}</p>
      </Bloque>
    </>
  );
};

// --- contexto de inclusion ---------------------------------------------------

/**
 * Las respuestas de seguimiento del tipo de discapacidad, al final de la hoja.
 *
 * Solo con la HV inclusiva activa. Si aun no hay respuestas, en el editor se
 * deja una invitacion pulsable (como "Tu nombre"); fuera del editor, nada: una
 * hoja de vida no puede imprimir un hueco con instrucciones.
 */
const ContextoInclusion = ({ doc, sel }: { doc: CVDocument; sel: PropsSeleccion }) => {
  if (!doc.is_inclusive) return null;
  const grupos = lineasContexto(doc.disability_type, doc.disability_details);
  if (grupos.length === 0 && !sel.onSeleccionar) return null;

  // Con una sola categoria el encabezado ya dice cual es; con varias (Múltiple)
  // cada grupo lleva su nombre para que se sepa a que se refiere cada respuesta.
  const conNombre = grupos.length > 1;

  return (
    <>
      <TituloSeccion>Contexto de inclusión</TituloSeccion>
      <Bloque ruta="disability_details" {...sel} bloque>
        {grupos.length === 0 && (
          <p className="doc-texto doc-vacio">
            Responde unas preguntas sobre tu discapacidad para que la empresa entienda tu contexto.
          </p>
        )}
        {grupos.map((grupo) => (
          <div className="doc-contexto-grupo" key={grupo.categoria}>
            {conNombre && <p className="doc-item-cargo">{grupo.categoria}</p>}
            {grupo.lineas.map((linea) =>
              linea.narrativa ? (
                <div key={linea.etiqueta}>
                  <p className="doc-etiqueta-logros">{linea.etiqueta}</p>
                  <p className="doc-texto">{linea.valor}</p>
                </div>
              ) : (
                <p className="doc-texto" key={linea.etiqueta}>
                  <b>{linea.etiqueta}</b>: {linea.valor}
                </p>
              ),
            )}
          </div>
        ))}
      </Bloque>
    </>
  );
};

// --- documento ---------------------------------------------------------------

export const Documento = ({
  doc,
  ...sel
}: PropsSeleccion & { doc: CVDocument }) => (
  <article className="doc-hoja">
    <Encabezado doc={doc} sel={sel} />

    {hayTexto(doc.resume) && (
      <>
        <TituloSeccion>Perfil profesional</TituloSeccion>
        <Bloque ruta="resume" {...sel}>
          <p className="doc-texto">{doc.resume}</p>
        </Bloque>
      </>
    )}

    {doc.experience.length > 0 && (
      <>
        <TituloSeccion>Experiencia laboral</TituloSeccion>
        {doc.experience.map((exp, i) => (
          <ItemExperiencia key={exp.id} exp={exp} indice={i} sel={sel} />
        ))}
      </>
    )}

    {doc.education.length > 0 && (
      <>
        <TituloSeccion>Educación</TituloSeccion>
        {doc.education.map((est, i) => (
          <ItemEstudio key={est.id} est={est} indice={i} sel={sel} />
        ))}
      </>
    )}

    <ListaEnLinea ruta="skills" titulo="Competencias" valores={doc.skills} sel={sel} />
    <ListaEnLinea
      ruta="soft_skills"
      titulo="Habilidades blandas"
      valores={doc.soft_skills}
      sel={sel}
    />
    <ListaEnLinea ruta="tools" titulo="Herramientas" valores={doc.tools} sel={sel} />

    {doc.languages.length > 0 && (
      <>
        <TituloSeccion>Idiomas</TituloSeccion>
        <Bloque ruta="languages" {...sel}>
          <p className="doc-texto">
            {doc.languages
              .filter((l) => hayTexto(l.name))
              // Sin nivel, solo el idioma: nada de "Inglés — " con el guion colgando.
              .map((l) => (hayTexto(l.level) ? `${l.name.trim()} — ${l.level.trim()}` : l.name.trim()))
              .join(" · ")}
          </p>
        </Bloque>
      </>
    )}

    {doc.others.length > 0 && (
      <>
        <TituloSeccion>Otros</TituloSeccion>
        {doc.others.map((otro, i) => (
          <div className="doc-item" key={otro.id}>
            <Bloque ruta={`others[${i}]`} {...sel} bloque>
              {hayTexto(otro.section_name) && (
                <p className="doc-item-cargo">{otro.section_name}</p>
              )}
              {hayTexto(otro.section_text) && (
                <p className="doc-texto">{otro.section_text}</p>
              )}
            </Bloque>
          </div>
        ))}
      </>
    )}

    <ContextoInclusion doc={doc} sel={sel} />
  </article>
);
